import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/store';
import Logo from 'components/Logo';
import { Button, Typography } from 'design-component';
import ProfileImg from 'assets/img/profile.svg';
import { palette } from 'styles/color';
import TooltipMenu from 'components/TooltipMenu';
import { userInfoSelector, logout } from 'store/slices/userInfo';
import { apiWrapper } from 'api';
import { LanguageSelect } from 'pages/Home';
import { languageSelector, setLanguage } from 'store/slices/languageSlice';
import i18n from 'locale';

export default function Header() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isLogged, name, email } = useAppSelector(userInfoSelector);
  const dispatch = useAppDispatch();
  const lang = useAppSelector(languageSelector);

  const handleLanguageChange = (lang: 'ko' | 'en') => {
    dispatch(setLanguage(lang));
    i18n.changeLanguage(lang);
  };

  const handleProfileClick = () => {
    const profileUrl = process.env.PROFILE_URL || 'https://www.polarisoffice.com/account#profile';
    window.location.href = profileUrl;
  };

  // 로그아웃용 iframe 생성
  const createIframeAndAppend = (src: string) => {
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = src;
    document.body.appendChild(iframe);

    return iframe;
  };

  const getRedirectUrl = (currentUrl: string): string => {
    if (currentUrl.includes('/_api/usage')) {
      return currentUrl.replace(/\/_api\/(usage)/, '/_api/reference');
    }
    return currentUrl;
  };

  const handleLogout = async () => {
    try {
      const currentUrl = window.location.href;
      const redirectUrl = getRedirectUrl(currentUrl);

      dispatch(logout());
      await apiWrapper(`/api/v1/datainsight/auth/requestLogout`, {
        method: 'POST'
      });

      const logoutUrl = `${process.env.LOGOUT_URL}${redirectUrl}`;
      createIframeAndAppend(logoutUrl);

      // if (redirectUrl.includes('/playground/doc-extract')) {
      //   window.location.reload();
      // }
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogin = () => apiWrapper(`/api/v1/datainsight/playground/userinfo`);

  return (
    <HeaderBase>
      <Logo />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }}>
        <Nav>
          {['/playground', '/api', '/documentation'].map((path) => {
            const resolvedPath = path === '/api' ? '/_api' : path;
            const isCurrentPath = location.pathname === resolvedPath;

            return (
              <NavItem
                key={resolvedPath}
                to={resolvedPath}
                selected={location.pathname.includes(resolvedPath)}
                onClick={(e) => {
                  if (isCurrentPath) {
                    e.preventDefault();
                  }
                }}>
                {path.replace('/', '').toUpperCase()}
              </NavItem>
            );
          })}
        </Nav>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <LanguageSelect value={lang} onChange={handleLanguageChange} position="bottom" />

          {isLogged ? (
            <TooltipMenu
              opener={
                <Button
                  variant="text"
                  color="primary"
                  style={{
                    padding: '0'
                  }}>
                  <img src={ProfileImg} alt="Profile" /> {/* 기본 프로필 이미지 */}
                </Button>
              }>
              <UserInfo>
                <Typography component="p" size="14px" weight={600} height="16px" truncate>
                  {name}
                </Typography>
                <Typography
                  component="p"
                  size="12px"
                  weight={400}
                  height="16px"
                  color={palette.gray[400]}>
                  {email}
                </Typography>
              </UserInfo>
              <TooltipMenu.MenuItem onClick={handleProfileClick}>
                <Button
                  variant="text"
                  color="default"
                  className="item"
                  style={{
                    minWidth: '180px'
                  }}>
                  <Typography component="span" size="14px" weight={400} height="16px">
                    {t('accountSettings')}
                  </Typography>
                </Button>
              </TooltipMenu.MenuItem>
              <TooltipMenu.MenuItem onClick={() => navigate('/documentation/terms_and_policies')}>
                <Button
                  variant="text"
                  color="default"
                  className="item"
                  style={{
                    minWidth: '180px'
                  }}>
                  <Typography component="span" size="14px" weight={400} height="16px">
                    {t('termsAndPolicies')}
                  </Typography>
                </Button>
              </TooltipMenu.MenuItem>
              <TooltipMenu.MenuItem onClick={handleLogout}>
                <Button
                  variant="text"
                  color="default"
                  className="item"
                  style={{
                    minWidth: '180px'
                  }}>
                  <Typography component="span" size="14px" weight={400} height="16px">
                    {t('logout')}
                  </Typography>
                </Button>
              </TooltipMenu.MenuItem>
            </TooltipMenu>
          ) : (
            <Button variant="outlined" color="primary" onClick={handleLogin}>
              LOG IN
            </Button>
          )}
        </div>
      </div>
    </HeaderBase>
  );
}

const HeaderBase = styled.div`
  width: 100%;
  height: 56px;
  display: flex;
  align-items: center;
  padding: 27px 34px;
  box-sizing: border-box;
  background-color: #ffffff;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;

  border-bottom: solid 1px #e6e6e6;
`;

const Nav = styled.nav`
  display: flex;
  gap: 40px;
  border-left: solid 1px ${palette.gray[200]};
  padding-left: 40px;
`;

const NavItem = styled(Link)<{ selected: boolean }>`
  min-width: 130px;

  color: ${({ selected }) => (selected ? palette.blue[500] : palette.gray[400])};
  font-weight: ${({ selected }) => (selected ? 700 : 500)};
  font-size: 14px;
  line-height: 20px;
  text-align: center;

  // &:hover {
  //   color: ${palette.blue[500]};
  // }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 4px;
  padding: 10px;
  border-bottom: 1px solid ${palette.gray[200]};
`;
